import styled from 'styled-components';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import DischargeQuestions from 'components/dischargeForm/DischargeQuestions';
import { buildAnsweredQuestionsFromFormValues } from 'components/dischargeForm/helpers';
import { AnswerType } from 'components/modals/QuestionList/QuestionList';
import Button from 'components/shared/Button';
import StandardModal, { Actions } from 'components/shared/StandardModal';
import { Form, FormProvider } from 'context/form';
import { FormHelpers, FormMeta, FormValues } from 'context/form/types';
import { useTranslation } from 'context/translations';
import Activity from 'models/Activity';
import { activityQueryKeys, updateActivity } from 'services/api/activity';
import { useToastActions } from 'stores/toastStore';

export type EditDischargeQuestionsModalType = {
  setShow: (b: boolean) => void;
  activity: Activity;
};

type Values = {
  questions: Record<string, string>;
};

type Meta = {
  isSubmitting: boolean;
};

export default function EditDischargeQuestionsModal({ setShow, activity }: EditDischargeQuestionsModalType) {
  const queryClient = useQueryClient();

  const { addToast } = useToastActions();
  const { translate } = useTranslation();

  const { mutate: mutateActivity } = useMutation({
    mutationFn: updateActivity,
  });

  const questions: AnswerType[] = activity.data.questions ?? [];

  const cancel = () => {
    setShow(false);
  };

  const handleSubmit = async (
    values: FormValues<Values>,
    _meta: FormMeta<Meta>,
    helpers: FormHelpers<Values, Meta>
  ) => {
    helpers.setMeta('isSubmitting', true);

    mutateActivity(
      {
        id: activity.id,
        questions: buildAnsweredQuestionsFromFormValues(questions, values.questions ?? {}),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: activityQueryKeys.base });
          cancel();
        },
        onError: () => {
          addToast({ text: 'Something went wrong. Please try again' });
        },
        onSettled: () => {
          helpers.setMeta('isSubmitting', false);
        },
      }
    );
  };

  const initialValues = questions?.reduce(
    (acc, { id, answer }) => ({
      ...acc,
      [id]: answer.text,
    }),
    {} as Record<string, string>
  );

  return (
    <StandardModal title={`Edit ${translate('Discharge')} Details`} onCancel={cancel}>
      <FormProvider onSubmit={handleSubmit} initialValues={{ questions: initialValues }}>
        {({ valid, dirty, meta, values }) => {
          const allRequiredQuestionsAnswered = questions
            .filter((q) => !q.config.optional)
            .every((q) => values.questions?.[q.id]);

          return (
            <Form>
              <QuestionContainer>
                <DischargeQuestions questions={questions} />
              </QuestionContainer>
              <Actions>
                <Button variant='ghost' onClick={cancel}>
                  Cancel
                </Button>
                <Button
                  type='submit'
                  disabled={!valid || !dirty || !allRequiredQuestionsAnswered}
                  loading={meta.isSubmitting}>
                  Confirm
                </Button>
              </Actions>
            </Form>
          );
        }}
      </FormProvider>
    </StandardModal>
  );
}

const QuestionContainer = styled.div`
  margin-bottom: 12px;
`;
